import React, { useState, useEffect, useMemo } from 'react';
import IconCircleWrapper from '../IconCircleWrapper';


// Process SVG: set dimensions, apply colors, and sanitize for Safari compatibility
const processSvg = (svgString: string, iconSize: number, color?: string): string => {
  try {
    // Create a temporary DOM element to properly parse the SVG
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');

    if (!svgElement) {
      return svgString; // Return original if parsing fails
    }

    // Set dimensions
    svgElement.setAttribute('width', iconSize.toString());
    svgElement.setAttribute('height', iconSize.toString());

    // Apply color if provided
    if (color) {
      // Find all elements that can be colored
      const elementsToColor = svgElement.querySelectorAll('path, circle, rect, polygon, polyline, line, ellipse, g');

      elementsToColor.forEach((element) => {
        // Only set fill/stroke if they don't already exist or are not 'none'
        const currentFill = element.getAttribute('fill');
        const currentStroke = element.getAttribute('stroke');

        if (!currentFill || (currentFill !== 'none' && currentFill !== 'transparent')) {
          element.setAttribute('fill', color);
        }

        if (currentStroke && currentStroke !== 'none') {
          element.setAttribute('stroke', color);
        }
      });

      // Also set the root SVG fill as fallback
      if (!svgElement.getAttribute('fill') || svgElement.getAttribute('fill') !== 'none') {
        svgElement.setAttribute('fill', color);
      }
    }

    // Return the serialized SVG
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgElement);
  } catch (error) {
    console.error('Error processing SVG:', error);
    // Fallback to simple string replacement if DOM parsing fails
    let result = svgString;

    if (color) {
      // More conservative regex replacements
      result = result.replace(/fill="(?!none)[^"]*"/gi, `fill="${color}"`);
      result = result.replace(/stroke="(?!none)[^"]*"/gi, `stroke="${color}"`);
    }

    // Replace dimensions more safely
    result = result.replace(/(<svg[^>]*)\s+width="[^"]*"/gi, '$1');
    result = result.replace(/(<svg[^>]*)\s+height="[^"]*"/gi, '$1');
    result = result.replace(/(<svg[^>]*)>/gi, `$1 width="${iconSize}" height="${iconSize}">`);

    return result;
  }
};

interface DynamicSvgIconProps {
  svgUrl: string;
  color?: string;
  backgroundColor?: string;
  size?: number;
  iconSize?: number;
}

const DynamicSvgIcon: React.FC<DynamicSvgIconProps> = ({
  svgUrl,
  color,
  backgroundColor,
  size = 40,
  iconSize = 24
}) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const isUrl = useMemo(() => {
    if (!svgUrl) return false;
    return svgUrl?.startsWith('http');
  }, [svgUrl]);

  const isSvgContent = useMemo(() => {
    if (!svgUrl) return false;
    return svgUrl?.startsWith('<svg');
  }, [svgUrl]);

  useEffect(() => {
    if (isSvgContent) {
      const processedSvg = processSvg(svgUrl, iconSize, color);
      setSvgContent(processedSvg);
      return;
    }

    if (!isUrl) return;

    const fetchSvg = async () => {
      try {
        const response = await fetch(svgUrl, { cache: 'force-cache' });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const svgText = await response.text();
        const processedSvg = processSvg(svgText, iconSize, color);
        setSvgContent(processedSvg);
      } catch (error) {
        console.error('Error fetching SVG:', error);
        setSvgContent(null);
      }
    };

    if (svgUrl) {
      fetchSvg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgUrl, color]);

  return (
    <IconCircleWrapper
      size={size}
      color={color}
      bgcolor={backgroundColor || (color ? `${color}33` : undefined)}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: iconSize,
          height: iconSize
        }}
        dangerouslySetInnerHTML={{ __html: svgContent || '' }}
      />
    </IconCircleWrapper>
  );
};

export default DynamicSvgIcon;